import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowLeft,
  Calendar,
  User,
  CreditCard,
  Gift,
  AlertCircle,
  Building2
} from 'lucide-react';
import axios from 'axios';
import { goBack } from '../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VerificarVoucherPage() {
  const { voucherId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verificarVoucher();
  }, [voucherId]);

  const verificarVoucher = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/beneficios/verificar-voucher/${voucherId}`);
      setResultado(response.data);
    } catch (err) {
      setError('Error al verificar el voucher');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (isoString) => {
    if (!isoString) return '';
    const fecha = new Date(isoString);
    return fecha.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
            <p className="text-gray-600">Verificando voucher...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 border-red-200">
          <CardContent className="py-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error de verificación</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button variant="outline" onClick={() => goBack()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const voucher = resultado?.voucher;
  const esValido = resultado?.valido;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Logo/Header */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Gift className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Verificación de Voucher</h1>
          <p className="text-sm text-gray-500">Colegio Profesional de TS de Lomas de Zamora</p>
        </div>

        {/* Resultado */}
        <Card className={esValido ? 'border-green-300' : 'border-red-300'}>
          <CardContent className="py-6">
            {/* Estado */}
            <div className="text-center mb-6">
              {esValido ? (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-700">VOUCHER VÁLIDO</h2>
                  <p className="text-gray-600 mt-1">Este voucher es auténtico y está vigente</p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <XCircle className="w-12 h-12 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-red-700">
                    {voucher ? 'VOUCHER VENCIDO' : 'VOUCHER NO VÁLIDO'}
                  </h2>
                  <p className="text-gray-600 mt-1">{resultado?.mensaje}</p>
                </>
              )}
            </div>

            {voucher && (
              <>
                {/* Código */}
                <div className="text-center mb-4">
                  <Badge variant="outline" className="text-lg px-4 py-1">
                    Código: {voucher.id}
                  </Badge>
                </div>

                {/* Datos del voucher */}
                <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Matriculado/a</p>
                      <p className="font-medium text-gray-900">{voucher.nombre}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Matrícula</p>
                      <p className="font-medium text-gray-900">{voucher.matricula}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Fecha de emisión</p>
                      <p className="font-medium text-gray-900">{formatFecha(voucher.fecha_emision)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Válido hasta</p>
                      <p className={`font-medium ${esValido ? 'text-gray-900' : 'text-red-600'}`}>
                        {formatFecha(voucher.fecha_vencimiento)}
                      </p>
                    </div>
                  </div>

                  {voucher.prestador_nombre && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Prestador</p>
                        <p className="font-medium text-gray-900">{voucher.prestador_nombre}</p>
                      </div>
                    </div>
                  )}

                  {voucher.origen && (
                    <div className="pt-2 border-t">
                      <Badge className={voucher.origen === 'rueda_turismo' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}>
                        {voucher.origen === 'rueda_turismo' ? 'Rueda de Turismo Social' : 'Convenio Directo'}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Mensaje de validación */}
                <div className={`mt-4 p-3 rounded-lg ${esValido ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex gap-2">
                    {esValido ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <p className={`text-sm ${esValido ? 'text-green-800' : 'text-red-800'}`}>
                      {esValido 
                        ? 'El/la matriculado/a se encuentra en condiciones de acceder a los beneficios acordados.'
                        : 'Este voucher ha expirado. El/la matriculado/a debe generar uno nuevo desde la plataforma.'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora<br />
          Gorriti 366, pb. Of. 1 y 2, Lomas de Zamora
        </p>
      </div>
    </div>
  );
}
